<!--
  TinoBriefing.vue — AI agent's daily/weekly/monthly briefing card.
  Shows a generated report and a period switcher (today / week / month).
-->
<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  period: {
    type: String,
    default: 'week',
  },
})

const emit = defineEmits(['update:period'])

const periods = ['today', 'week', 'month']
const dropdownOpen = ref(false)

const briefingText = computed(() => t(`dashboard.tino.briefing.${props.period}`))

function selectPeriod(p) {
  emit('update:period', p)
  dropdownOpen.value = false
}
</script>

<template>
  <VCard class="tino-briefing-card" flat>
    <div class="tino-briefing-inner">
      <!-- Avatar -->
      <div class="tino-avatar">
        🤖
      </div>

      <!-- Text -->
      <div class="tino-briefing-body">
        <p class="tino-briefing-text">{{ briefingText }}</p>
        <p class="tino-briefing-meta">{{ t('dashboard.tino.agentName') }} · {{ t('dashboard.tino.briefingReport') }}</p>
      </div>

      <!-- Period switcher -->
      <div class="tino-period-switcher">
        <VMenu v-model="dropdownOpen" location="bottom end" :close-on-content-click="false">
          <template #activator="{ props: menuProps }">
            <VBtn
              v-bind="menuProps"
              variant="tonal"
              color="default"
              density="compact"
              size="small"
              append-icon="mdi-chevron-down"
            >
              {{ t(`dashboard.tino.period.${period}`) }}
            </VBtn>
          </template>
          <VList density="compact" min-width="130">
            <VListItem
              v-for="p in periods"
              :key="p"
              :active="p === period"
              color="primary"
              :value="p"
              @click="selectPeriod(p)"
            >
              <VListItemTitle class="text-body-2">
                {{ t(`dashboard.tino.period.${p}`) }}
              </VListItemTitle>
            </VListItem>
          </VList>
        </VMenu>
      </div>
    </div>
  </VCard>
</template>

<style scoped lang="scss">
.tino-briefing-card {
  border: 1px solid rgba(var(--v-border-color), 0.1);
  border-radius: 16px !important;
  background: rgb(var(--v-theme-surface));
}

.tino-briefing-inner {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px;
}

.tino-avatar {
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
}

.tino-briefing-body {
  flex: 1;
  min-width: 0;
}

.tino-briefing-text {
  font-size: 0.875rem;
  line-height: 1.6;
  color: rgba(var(--v-theme-on-surface), 0.82);
  margin: 0;
}

.tino-briefing-meta {
  font-size: 0.72rem;
  color: rgba(var(--v-theme-on-surface), 0.4);
  margin: 6px 0 0;
}

.tino-period-switcher {
  flex-shrink: 0;
}
</style>
